import { useEffect, useRef, useState } from 'react'
import { formatCurrency } from '@/lib/utils'

// easeOutExpo: fast spin, long settle — gives the "odometer / slot machine" feel
const easeOutExpo = (t: number) => (t >= 1 ? 1 : 1 - Math.pow(2, -10 * t))

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

const now = () =>
  typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now()

/**
 * Currency value that rolls (counts up/down) whenever `value` changes.
 * Renders the exact value on first mount (no entrance animation), so it only
 * animates on later changes — e.g. switching the 7d / 30d / 90d range.
 * Uses requestAnimationFrame (no synchronous setState in the effect body).
 */
export function AnimatedCurrency({
  value,
  className,
  durationMs = 800,
}: {
  value: number
  className?: string
  durationMs?: number
}) {
  const [display, setDisplay] = useState(value)
  const prev = useRef(value)
  const raf = useRef<number | null>(null)

  useEffect(() => {
    const from = prev.current
    const to = value
    prev.current = to
    if (from === to) return

    const ms = prefersReducedMotion() ? 0 : durationMs
    const start = now()

    const tick = () => {
      const t = ms <= 0 ? 1 : Math.min((now() - start) / ms, 1)
      if (t >= 1) {
        setDisplay(to)
        raf.current = null
        return
      }
      setDisplay(from + (to - from) * easeOutExpo(t))
      raf.current = requestAnimationFrame(tick)
    }
    raf.current = requestAnimationFrame(tick)

    return () => {
      if (raf.current != null) cancelAnimationFrame(raf.current)
    }
  }, [value, durationMs])

  return <span className={className}>{formatCurrency(display)}</span>
}
